import React, { Suspense, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ArrowLeft, Split, X } from 'lucide-react';
import type { Accommodation, Destination, Transport, TripConfig, User } from '../types';
import type { Junction } from '../../functions/src/junctionPolicy.mjs';
import { destinationKey } from '../utils/destinationItems';
import { hasTravellerDifferences, boundaryScope, branchJunctionRows, junctionDate, junctionPosition, junctionLabel, branchAncestors, canEnterBranch, personalDestinations, personalRouteSections, personalRouteVisits, projectBranchTrip, validateBranch, type ItineraryBranch } from '../utils/itineraryBranches';
import { FIELD, fieldBorder } from './UI';
import { TravellerPill } from './UserAvatar';
import { TripManagementForm } from './Itinerary';
import { ImportItineraryModal } from './LazyModals';
import { saveAccommodationBooking, saveTransportBooking } from '../utils/itineraryWrites';
import { userMessage } from '../utils/userMessage';
import './ItineraryBranches.css';
import { ItineraryJunctionLine } from './ItineraryJunctionLine';

function BranchDialog({ children, onClose, labelledBy }: { children: React.ReactNode; onClose: () => void; labelledBy: string }) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const dialog = ref.current!;
    dialog.showModal();
    return () => dialog.close();
  }, []);
  return createPortal(<dialog ref={ref} aria-labelledby={labelledBy} onCancel={event=>{event.preventDefault();onClose();}} className="itinerary-branch-editor">{children}</dialog>, document.getElementById('import-modal-root') ?? document.body);
}

export interface BranchTimelineContext {
  key: string;
  direction: number;
  summary: TripConfig;
  controls: React.ReactNode;
  entry: React.ReactNode;
  exit: React.ReactNode;
  before: (destination: Destination) => React.ReactNode;
  during?: (destination: Destination) => React.ReactNode;
  after: (destination: Destination) => React.ReactNode;
  editDestination: (destination: Destination) => void;
  canEditDestination: boolean;
  isBranch: boolean;
  preview: boolean;
  personalSegment?: boolean;
  hideHeader?: boolean;
  returns: boolean;
  saveConfig: (config: TripConfig) => Promise<boolean>;
  saveAccommodationDraft: (previous: Accommodation | null, next: Accommodation | null) => Promise<Accommodation | null>;
  saveTransportDraft: (date: string, previous: Transport | null, next: Transport | null) => Promise<Transport | null>;
}
export interface BranchWorkspaceData {
  branches: ItineraryBranch[];
  onChange?: (branches: ItineraryBranch[]) => void;
  persistence?: {
    configs: Record<string,TripConfig>;
    loading: boolean;
    error: string;
    selected: string | null;
    select: (id:string|null)=>void;
    saveRoot: (config:TripConfig)=>Promise<boolean>;
    save: (branch:ItineraryBranch)=>Promise<void>;
    leave: (branch:ItineraryBranch,successorId?:string)=>Promise<void>;
  };
}

function BranchLine({ branch, travellers, events = [], rejoining = false, onOpen, placement, rootStart, rootBoundary }: {
  branch: ItineraryBranch; travellers: User[]; events?: Junction[]; rejoining?: boolean; onOpen?: () => void;
  rootStart?: string; rootBoundary?: 'start' | 'end';
  placement?: { destination: Destination; position: ReturnType<typeof junctionPosition> };
}) {
  const rows = branchJunctionRows(branch, events, rejoining ? 'rejoin' : 'split')
    .filter(row => !placement || junctionPosition(row.date, placement.destination, rejoining ? 'before' : 'after') === placement.position);
  const inset = placement?.position === 'during';
  const splits = rootStart && rejoining ? branchJunctionRows(branch, events, 'split') : [];
  return <>{rows.map(row => {
  const firstJoin = !!rootStart && row.ids.length > 0 && row.ids.every(id =>
    splits.some(split => split.ids.includes(id) && !!split.date && split.date <= rootStart));
  const label = junctionLabel(rejoining ? 'rejoin' : 'split', rootBoundary, firstJoin);
  if (!label) return null;
  const people = travellers.filter(p => row.ids.includes(p.id));
  const when = row.date ? ` on ${junctionDate(row.date)}` : '';
  return <ItineraryJunctionLine key={`${row.date}:${row.ids.join()}`} label={`${label}${when}`}
    people={people} rejoining={rejoining} inset={inset} onOpen={onOpen} />;
  })}</>;
}

/** Core itinerary UI, fed through an explicit workspace. Persistence/access and
 * notifications are intentionally not inferred from the shared TripConfig. */
export function ItineraryBranchWorkspace({ trip: initialTrip, user, workspace, children }: {
  trip: TripConfig; user: User; workspace: BranchWorkspaceData;
  children: (config: TripConfig, timeline: BranchTimelineContext) => React.ReactNode;
}) {
  const { branches, onChange, persistence } = workspace;
  const [trip, setTrip] = useState(initialTrip);
  const [direction, setDirection] = useState(1);
  const [localSelected, setLocalSelected] = useState<string | null>(null);
  const selected = persistence ? persistence.selected : localSelected;
  const setSelected = persistence?.select ?? setLocalSelected;
  const [requestedFilter, setFilter] = useState<'all' | 'me'>('all');
  const showTravellerFilter = hasTravellerDifferences(trip, branches, persistence?.configs);
  const filter = showTravellerFilter ? requestedFilter : 'all';
  const [draft, setDraft] = useState<ItineraryBranch | null>(null);
  const [step, setStep] = useState<'dates' | 'destinations' | 'travellers'>('dates');
  const [error, setError] = useState('');
  const [leaveOpen, setLeaveOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [successor, setSuccessor] = useState('');
  const [importScopeId, setImportScopeId] = useState<string | null>(null);
  const joinTarget = useRef<string | null>(null);
  const canOpen = (id:string) => canEnterBranch(branches,id,user.id) && (!persistence || !!persistence.configs[id]);
  const active = selected && canOpen(selected) ? branches.find(b => b.id === selected)! : null;
  const canManage = !!active && (!persistence || active.ownerId === user.id || !trip.memberIds.includes(active.ownerId ?? ''));
  const canImport = !!active && active.ownerId === user.id && !!persistence && !persistence.loading && !persistence.error;
  const canShowDraft = !!draft && (!persistence || ((!draft.revision || !!persistence.configs[draft.id]) && (!draft.parentId || !!persistence.configs[draft.parentId])));
  const parent = active?.parentId ? branches.find(b => b.id === active.parentId) : null;
  const parentStops = parent?.destinations ?? trip.destinations;
  const rejoin = active && parentStops.find(d => destinationKey(d) === active.rejoinVisitId);
  const stops = filter === 'me' ? personalDestinations(trip, branches, user.id) : active?.destinations ?? trip.destinations;
  const projected = projectBranchTrip(trip, stops);
  const config = active ? persistence?.configs[active.id] ?? { ...projected, accommodations:active.accommodations || [], itinerary:active.itinerary || {} } : projected;
  const visibleChildren = filter === 'all' ? branches.filter(b => b.parentId === (active?.id ?? null)) : [];
  // Top-level events ride on summaries; nested ones are only on the parent branch.
  const eventsFor = (b: ItineraryBranch): Junction[] => (active
    ? (persistence?.configs[active.id] as { childJunctions?: Record<string, Junction[]> } | undefined)?.childJunctions?.[b.id]
    : b.junctions) ?? [];
  const activeKey = `${user.id}:${filter}:${active?.id ?? 'main'}`;
  const heading = useRef<HTMLHeadingElement>(null);

  useEffect(() => { setTrip(initialTrip); }, [initialTrip]);
  useEffect(() => { if (!showTravellerFilter) setFilter('all'); }, [showTravellerFilter]);
  useEffect(() => { setDraft(null); setLeaveOpen(false); }, [user.id, trip.id]);
  useEffect(() => { if (!canImport || importScopeId !== config.id) setImportScopeId(null); }, [canImport, config.id, importScopeId, user.id]);
  useEffect(() => { if(draft && !canShowDraft)setDraft(null); if(!active)setLeaveOpen(false); }, [canShowDraft,active]);
  useEffect(() => {
    heading.current?.focus({ preventScroll: true });
    if (joinTarget.current) {
      const key = joinTarget.current;
      const node = [...document.querySelectorAll<HTMLElement>('[data-itinerary-visit]')].find(el => el.dataset.itineraryVisit === key);
      node?.scrollIntoView({ block: 'center', behavior: 'instant' });
      joinTarget.current = null;
    }
  }, [activeKey]);

  function open(id: string | null, visitId?: string) {
    if (id && !canOpen(id)) return;
    setDirection(id && branches.find(b => b.id === id)?.parentId === (active?.id ?? null) ? 1 : -1);
    joinTarget.current = visitId ?? null;
    setSelected(id); setFilter('all'); setError('');
  }
  function edit(branch: ItineraryBranch) { setDraft({...structuredClone(branch),memberIds:branch.memberIds.filter(id=>trip.memberIds.includes(id))}); setStep('dates'); setError(''); }
  function create() {
    const route = active?.destinations ?? trip.destinations;
    const first = route[0];
    if (!first) return;
    setDraft({ id: crypto.randomUUID(), parentId: active?.id ?? null, title: '', memberIds: [user.id], splitVisitId: destinationKey(first), rejoinVisitId: null, rejoinDate: '', destinations: [] });
    setStep('dates'); setError('');
  }
  async function save(data: TripConfig) {
    if (!draft) return false;
    const next = { ...draft, title: data.title, destinations: data.destinations.map(d => ({ ...d, id: d.id || crypto.randomUUID() })) };
    const problem = validateBranch(next, branches, trip);
    if (problem) { setError(problem); return false; }
    setBusy(true);
    try {
      if (persistence) await persistence.save(next);
      else onChange?.([...branches.filter(b => b.id !== draft.id), { ...next, destinations: [...next.destinations].sort((a,b) => a.arrivalDate.localeCompare(b.arrivalDate)) }]);
      setSelected(next.memberIds.includes(user.id) ? next.id : null); setFilter('all'); setDraft(null);
      return true;
    } catch (e) { setError(userMessage(e,'Could not save this branch. Reopen its latest version before trying again.')); return false; }
    finally { setBusy(false); }
  }
  async function leave() {
    if (!active) return;
    if (branches.some(b => b.parentId === active.id && b.memberIds.includes(user.id))) { setError('Leave your nested branches first.'); return; }
    setBusy(true);
    try {
      if (persistence) await persistence.leave(active,successor);
      else onChange?.(branches.map(b => b.id === active.id ? { ...b, memberIds: b.memberIds.filter(id => id !== user.id) } : b));
      setSelected(active.parentId); setLeaveOpen(false); setError('');
    } catch (e) { setError(userMessage(e,'Could not leave this branch. Check the organiser choice and reopen its latest version.')); }
    finally { setBusy(false); }
  }
  async function saveConfig(next: TripConfig) {
    if (persistence) {
      if (next.id === initialTrip.id) return persistence.saveRoot(next);
      if (!active) return false;
      try { await persistence.save({...active,title:next.title,destinations:next.destinations}); return true; }
      catch(e) { setError(userMessage(e,'Could not save this branch. Reopen its latest version before trying again.')); return false; }
    }
    if (active) {
      onChange?.(branches.map(b=>b.id === active.id ? {...b,destinations:next.destinations,accommodations:next.accommodations || [],itinerary:next.itinerary || {}} : b));
    } else {
      setTrip(next);
    }
    return true;
  }
  async function saveAccommodationDraft(previous: Accommodation | null, next: Accommodation | null) {
    if (persistence) return saveAccommodationBooking(config,previous,next,user.id);
    await saveConfig({...config,accommodations:[...(config.accommodations || []).filter(a=>a.id!==previous?.id),...(next?[next]:[])]});
    return next;
  }
  async function saveTransportDraft(date: string, previous: Transport | null, next: Transport | null) {
    if (persistence) {
      const visits = personalRouteVisits(trip,branches,persistence.configs,user.id);
      if (!previous && (persistence.loading || persistence.error || !visits)) throw new Error('Wait for your branches to load before saving transport.');
      return saveTransportBooking(!previous && next ? boundaryScope(next,visits!) ?? config : config,date,previous,next,user.id);
    }
    const itinerary = {...config.itinerary};
    for (const [key, day] of Object.entries(itinerary)) itinerary[key] = {...day,transports:(day.transports || []).filter(t=>t.id!==previous?.id)};
    if (next) {
      const target = /^\d{4}-\d{2}-\d{2}/.test(next.dep || '') ? next.dep.slice(0,10) : date;
      itinerary[target] = {...itinerary[target],date:target,transports:[...(itinerary[target]?.transports || []),next]};
    }
    await saveConfig({...config,itinerary});
    return next;
  }
  const field = `${FIELD} ${fieldBorder(false, true)}`;
  const draftParent = draft?.parentId ? branches.find(b => b.id === draft.parentId) : null;
  const draftStops = draftParent?.destinations ?? trip.destinations;
  const choices = trip.travellers.filter(p => trip.memberIds.includes(p.id) && (!draftParent || draftParent.memberIds.includes(p.id)));
  const label = 'block text-xs font-bold uppercase tracking-wide text-app-sub mb-2';

  const renderJunctions = (destination: Destination, position: ReturnType<typeof junctionPosition>) => {
    const boundary = !active
      ? position === 'before' && destinationKey(destination) === destinationKey(trip.destinations[0]) ? 'start'
        : position === 'after' && destinationKey(destination) === destinationKey(trip.destinations.at(-1)!) ? 'end' : undefined
      : undefined;
    return visibleChildren.filter(b=>b.memberIds.length).flatMap(b=>(['split','rejoin'] as const).map(kind=> {
      const anchor = kind === 'rejoin' ? b.rejoinVisitId : b.splitVisitId;
      return anchor === destinationKey(destination) ? <BranchLine key={`${b.id}:${kind}`} branch={b}
        events={eventsFor(b)} travellers={trip.travellers} rejoining={kind === 'rejoin'}
        rootStart={!active ? trip.destinations[0]?.arrivalDate : undefined} rootBoundary={boundary}
        placement={{destination,position}} onOpen={canOpen(b.id)?()=>open(b.id):undefined}/> : null;
    }));
  };
  const controls = <>
    {(persistence?.error || (error && !draft && !leaveOpen)) && <p role="alert" className="text-rose-700">{persistence?.error || error}</p>}
    <div className="itinerary-branch-toolbar"><button disabled={busy || persistence?.loading} className="itinerary-branch-link" onClick={create}><Split size={17} aria-hidden="true" />Create branch</button>{showTravellerFilter && <div className="itinerary-branch-filter" role="group" aria-label="Itinerary view">{(['all','me'] as const).map(value => <button key={value} aria-pressed={filter === value} onClick={() => { setFilter(value); setSelected(null); }}>{value === 'all' ? 'All' : 'Just me'}</button>)}</div>}</div>
    {active && visibleChildren.filter(b=>b.memberIds.length && !active.destinations.some(d=>destinationKey(d)===b.splitVisitId)).map(b=><BranchLine key={b.id} branch={b} events={eventsFor(b)} travellers={trip.travellers} onOpen={canOpen(b.id)?()=>open(b.id):undefined}/>)}
    {active && <div className="itinerary-branch-heading"><nav aria-label="Branch path"><button onClick={() => open(null)}>Main itinerary</button>{branchAncestors(branches,active.id).map(b => <React.Fragment key={b.id}><span>/</span>{b.id === active.id ? <span aria-current="page">{b.title}</span> : <button onClick={() => open(b.id)}>{b.title}</button>}</React.Fragment>)}</nav><h2 ref={heading} tabIndex={-1} className="font-serif text-3xl font-bold">{active.title}</h2><div className="itinerary-branch-toolbar"><button className="itinerary-branch-link" onClick={() => open(active.parentId)}><ArrowLeft size={16} />Back to {parent?.title || 'main itinerary'}</button>{canManage && <button className="itinerary-branch-link" onClick={() => edit(active)}>Edit branch</button>}</div></div>}
    {active && <div className="itinerary-branch-toolbar mb-4">
      {canImport && <button className="itinerary-branch-link" onClick={() => setImportScopeId(config.id)}>Import itinerary</button>}
      <button className="itinerary-branch-leave" onClick={() => { setLeaveOpen(true); setSuccessor(''); setError(''); }}>Leave this branch</button>
    </div>}
    {canImport && importScopeId === config.id && <Suspense fallback={<p role="status">Opening import…</p>}>
      <ImportItineraryModal key={config.id} tripConfig={config} currentUser={user}
        onUpdateConfig={() => {}} onClose={() => setImportScopeId(null)} />
    </Suspense>}
    {draft && canShowDraft && <BranchDialog labelledBy="branch-editor-heading" onClose={() => { if(!busy)setDraft(null); }}>
      <header><h2 id="branch-editor-heading" className="font-serif text-3xl font-bold">{step === 'dates' ? 'Your branch' : step === 'destinations' ? 'Your destinations' : 'Who’s splitting off?'}</h2><button className="min-w-11 min-h-11 flex items-center justify-center text-app-sub" aria-label="Close branch editor" disabled={busy} onClick={() => setDraft(null)} autoFocus><X size={22} /></button></header>
      <p className="text-xs text-app-sub text-right mb-5">Step {['dates','destinations','travellers'].indexOf(step) + 1} of 3</p>
      <TripManagementForm key={draft.id} mode="wizard"
        config={{ ...trip, title:draft.title, destinations:draft.destinations,
          startDate:draft.destinations[0]?.arrivalDate || draftStops.find(d=>destinationKey(d)===draft.splitVisitId)?.departureDate || trip.startDate,
          endDate:draft.destinations.at(-1)?.departureDate || draft.rejoinDate || trip.endDate }}
        onWizardStepChange={setStep}
        onChange={data=>setDraft(current=>current ? {...current,title:data.title,destinations:data.destinations} : null)}
        onSave={save} onCancel={()=>{if(!busy)setDraft(null);}}
        branchSetup={<div className="space-y-5"><fieldset><legend className={label}>Travellers</legend><div className="flex flex-wrap gap-2">{choices.map(p => <TravellerPill key={p.id} user={p} selected={draft.memberIds.includes(p.id)} onClick={() => setDraft({...draft,memberIds:draft.memberIds.includes(p.id) ? draft.memberIds.filter(id=>id!==p.id) : [...draft.memberIds,p.id]})} />)}</div></fieldset>
      <label><span className={label}>Leave the route at</span><select className={field} value={draft.splitVisitId} onChange={e=>setDraft({...draft,splitVisitId:e.target.value})}>{draftStops.map(d=><option key={destinationKey(d)} value={destinationKey(d)}>{d.name}</option>)}</select></label>
      <label className="flex gap-3 items-center"><input type="checkbox" checked={!!draft.rejoinVisitId} onChange={e=> { const d=draftStops.at(-1)!; setDraft({...draft,rejoinVisitId:e.target.checked?destinationKey(d):null,rejoinDate:e.target.checked?d.arrivalDate:''}); }} />Rejoin later</label>
      {draft.rejoinVisitId && <><label><span className={label}>Rejoin at</span><select className={field} value={draft.rejoinVisitId} onChange={e=> { const d=draftStops.find(d=>destinationKey(d)===e.target.value)!;setDraft({...draft,rejoinVisitId:e.target.value,rejoinDate:d.arrivalDate}); }}>{draftStops.map(d=><option key={destinationKey(d)} value={destinationKey(d)}>{d.name}</option>)}</select></label><label><span className={label}>Rejoin date</span><input type="date" className={field} value={draft.rejoinDate} onChange={e=>setDraft({...draft,rejoinDate:e.target.value})} /></label></>}
      {error && <p role="alert" className="text-rose-700">{error}</p>}</div>}
      />
    </BranchDialog>}
    {leaveOpen && active && <BranchDialog labelledBy="branch-leave-heading" onClose={() => {if(!busy)setLeaveOpen(false);}}><h2 id="branch-leave-heading" className="font-serif text-3xl font-bold mb-4">Leave this branch?</h2><p>Bookings and debts stay unchanged. You will no longer be able to view this branch or its expenses.</p>{persistence && active.ownerId === user.id && active.memberIds.length > 1 && <label className="block mt-4"><span className={label}>Next branch organiser</span><select className={field} value={successor} onChange={e=>setSuccessor(e.target.value)}><option value="">Choose a traveller</option>{trip.travellers.filter(p=>p.id!==user.id&&active.memberIds.includes(p.id)).map(p=><option key={p.id} value={p.id}>{p.name}</option>)}</select></label>}{error&&<p role="alert" className="text-rose-700 mt-4">{error}</p>}<footer><button disabled={busy} className="itinerary-branch-link" autoFocus onClick={()=>setLeaveOpen(false)}>Cancel</button><button disabled={busy} className="itinerary-branch-primary" onClick={leave}>{busy?'Leaving…':'Leave branch'}</button></footer></BranchDialog>}
  </>;
  if (persistence && filter === 'me') {
    const sections=personalRouteSections(trip,branches,persistence.configs,user.id);
    const visits=personalRouteVisits(trip,branches,persistence.configs,user.id) ?? [];
    if (persistence.loading || persistence.error || !sections) return <div className="py-4">{controls}
      {!persistence.error && <p role="status" className="py-6 text-app-sub">Loading your itinerary…</p>}
    </div>;
    if (!sections.length) return <div className="py-4">{controls}</div>;
    return <div className="h-full overflow-y-auto overflow-x-hidden no-scrollbar">{sections.map(({source,view},index)=><React.Fragment key={`${source.id}:${index}`}>
      {children(view,{
        key:`personal:${source.id}:${index}`,direction,summary:trip,controls:index===0?controls:null,
        personalSegment:true,hideHeader:index>0,isBranch:!!source.branchId,preview:false,returns:false,
        entry:null,exit:null,before:()=>null,after:()=>null,canEditDestination:false,editDestination:()=>{},
        saveConfig:next=>next.id===trip.id?persistence.saveRoot(next):Promise.resolve(false),
        saveAccommodationDraft:(previous,next)=>saveAccommodationBooking(source,previous,next,user.id),
        // A new leg leaving or rejoining the shared route is the branch's, whichever
        // section it was added from; existing bookings never move scope.
        saveTransportDraft:(date,previous,next)=>saveTransportBooking(!previous && next ? boundaryScope(next,visits) ?? source : source,date,previous,next,user.id),
      })}
    </React.Fragment>)}</div>;
  }
  return children(config, {
    key:activeKey, direction, summary:trip, controls, saveConfig, saveAccommodationDraft, saveTransportDraft,
    isBranch:!!active,preview:!persistence,returns:!!rejoin,
    entry:active ? <div className="itinerary-branch-entry" aria-hidden="true"><svg><path d="M-30 0 H-18 C24 0 24 24 24 54 V64" /></svg></div>:null,
    exit:active ? rejoin ? <div className="itinerary-branch-exit"><svg aria-hidden="true"><path d="M24 0 V12 C24 48 0 56 -18 56 H-30" /></svg><div><strong>Rejoining in {rejoin.name}</strong><p>{new Date(active.rejoinDate + 'T12:00:00Z').toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' })}</p><button className="itinerary-branch-link" onClick={()=>open(active.parentId,destinationKey(rejoin))}><ArrowLeft size={16}/>View rejoin</button></div></div>:<p className="text-sm text-app-sub pl-[72px] py-6">This branch finishes separately.</p>:null,
    before:destination=>renderJunctions(destination,'before'),
    during:destination=>renderJunctions(destination,'during'),
    after:destination=>renderJunctions(destination,'after'),
    canEditDestination:canManage,
    editDestination:()=> { if(active) {edit(active);} },
  });
}
